import { Mouse, TouchApp } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { type Layout } from 'plotly.js';
import React, { type CSSProperties, useEffect, useRef, useState } from 'react';
import { type PlotParams } from 'react-plotly.js';

import { EStylings } from '../enums/EStylings';
import { updateState, updateStateAttribute } from '../helpers/helper';
import { useAppSelector } from '../hooks/hooks';
import CHorizontalScrollContainer from './CHorizontalScrollContainer';
import CLocalizedText from './CLocalizedText';
import CModal from './CModal';
import COverlayMessage from './COverlayMessage';
import CPageModule, { type IPageModule } from './CPageModule';
import './CPageModulePlot.css';
import CPlot from './CPlot';
import CStatefulContainer from './CStatefulContainer';
import { type IOverlayMessage } from './IOverlayMessage';
import TimestampComponent from './TimestampComponent';

interface Props
  extends PlotParams,
    Pick<CSSProperties, 'aspectRatio' | 'height'>,
    Pick<IPageModule, 'apiRequestState'> {
  aspectRatio?: number;
  showWhen?: boolean;
  clickToInteract?: boolean;
  onCloseModal?: () => void;
  disablePlotModal?: boolean;
  fitToViewport?: boolean;
  dataTimestamp?: Date;
  overlayMessage?: IOverlayMessage;
}

interface State {
  data: PlotParams['data'];
  showModal?: boolean;
}

export default function CPageModulePlot(props: Props) {
  const classNames: string[] = [];
  const aspectRatio = Number.isNaN(props.aspectRatio) ? 2 : (props.aspectRatio ?? 2);

  const [state, setState] = useState<State>({ data: [] });
  const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
  const { isTouchscreen } = useAppSelector((state) => state.dashboard);
  const measurementBox = useRef<HTMLDivElement>(null);
  const measurementBox2 = useRef<HTMLDivElement>(null);
  const propsLayout: Partial<Layout> = { ...props.layout };

  const shouldOpenPlotModal = [isTouchscreen, props.clickToInteract].some(Boolean);
  const shouldShowDesktopModal = !isTouchscreen && [props.clickToInteract].some(Boolean);

  propsLayout.width = undefined;
  propsLayout.height = undefined;

  if (props.className) classNames.push(props.className);

  const openModal = () => {
    updateStateAttribute<State>({ showModal: true }, setState);
  };

  const mainPlot = (
    <div
      ref={measurementBox}
      className={'relative w-full min-w-0 rounded-lg overflow-clip bg-gray-50'}
      style={{
        aspectRatio,
        height: props.height ?? EStylings.MIN_CONTENT_WIDTH / aspectRatio
      }}
      onClick={openModal}
    >
      <CPlot
        data={state.data}
        layout={{
          ...propsLayout,
          width: measurementBox.current?.getBoundingClientRect().width ?? 0,
          height: measurementBox.current?.getBoundingClientRect().height ?? 0,
          legend: props?.fitToViewport
            ? {
                y: -0.3,
                yanchor: 'bottom',
                ...propsLayout.legend
              }
            : propsLayout.legend,

          margin: props.fitToViewport
            ? {
                t: 0,
                b: 0,
                l: 0,
                r: 0
              }
            : {
                t: 0,
                b: 0,
                l: 0,
                r: Math.max(0, 2000 - (measurementBox.current?.getBoundingClientRect().width ?? 0)) / 15
              }
        }}
        config={{
          ...props.config
        }}
      />

      {shouldOpenPlotModal && (
        <div
          className="absolute cursor-pointer top-0"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0)',
            width: measurementBox.current?.getBoundingClientRect().width ?? 0,
            height: measurementBox.current?.getBoundingClientRect().height ?? 0
          }}
          onClick={() => {
            openModal();
          }}
        />
      )}

      {props.overlayMessage && <COverlayMessage message={props.overlayMessage} />}
    </div>
  );

  const modalPlot = (
    <div
      ref={measurementBox2}
      className="relative bg-gray-50 rounded-lg w-full overflow-clip"
      style={{
        aspectRatio,
        height: props.height ?? (1100 - 8 * remInPx) / aspectRatio
      }}
    >
      <CPlot
        data={state.data}
        layout={{
          ...propsLayout,
          width: measurementBox2.current?.getBoundingClientRect().width ?? 0,
          height: measurementBox2.current?.getBoundingClientRect().height ?? 0,
          margin: {
            t: 0,
            b: 0,
            l: 0,
            r: Math.max(0, 2000 - (measurementBox2.current?.getBoundingClientRect().width ?? 0)) / 15
          }
        }}
      />

      {props.overlayMessage && <COverlayMessage message={props.overlayMessage} />}
    </div>
  );

  useEffect(() => {
    setTimeout(() => {
      // We set timeout to allow the plot framework to render, before parsing data. This is a hack to support fade-ins
      updateState<State>({ data: props.data }, state, setState);
    });
  }, [props.data, state.showModal]);

  return (
    <CStatefulContainer
      apiRequestState={props.apiRequestState}
      showContent={props.showWhen}
      errorMessage={
        <CPageModule style={{ paddingTop: 0 }}>
          <Alert severity={'error'}>{props.apiRequestState?.strError}</Alert>{' '}
        </CPageModule>
      }
    >
      {/* PLOT STUFF */}
      <div className={classNames.join(' ')} style={{ position: 'relative' }}>
        {props.fitToViewport ? (
          <CPageModule style={{ paddingTop: 0 }} submodule>
            {mainPlot}
          </CPageModule>
        ) : isTouchscreen && props.disablePlotModal === true ? (
          <CHorizontalScrollContainer sidePadding={<div className={'p-2 sm:p-4'} />} extendTouchscreenScrollArea>
            {modalPlot}
          </CHorizontalScrollContainer>
        ) : (
          <CHorizontalScrollContainer>{mainPlot}</CHorizontalScrollContainer>
        )}
        {shouldOpenPlotModal && props.disablePlotModal !== true && (
          <CPageModule style={{ paddingBottom: 0, paddingTop: 0 }}>
            <div
              className={'text-xs text-center opacity-50 mt-4 flex gap-3 items-center justify-center cursor-pointer'}
              onClick={openModal}
            >
              {isTouchscreen ? <TouchApp /> : <Mouse />}
              <span>
                {isTouchscreen ? (
                  <CLocalizedText dictKey={'messageTapToOpenInteractivePlot'} />
                ) : (
                  <CLocalizedText dictKey={'generic.plots.clickToInteract'} />
                )}
              </span>
            </div>
          </CPageModule>
        )}
      </div>

      {/* MOBILE MODAL */}
      {isTouchscreen && props?.disablePlotModal !== true && (
        <CModal
          onClose={() => {
            updateState<State>({ showModal: false }, state, setState);
            props.onCloseModal?.();
          }}
          style={{ paddingLeft: 0, paddingRight: 0 }}
          open={!!state.showModal}
        >
          <CStatefulContainer
            apiRequestState={props.apiRequestState}
            errorMessage={
              <CPageModule style={{ paddingTop: 0, paddingBottom: 0 }}>
                <Alert severity={'error'}>{props.apiRequestState?.strError}</Alert>
              </CPageModule>
            }
          >
            <CHorizontalScrollContainer sidePadding={<div className={'p-2 sm:p-4'} />} extendTouchscreenScrollArea>
              {modalPlot}
            </CHorizontalScrollContainer>
          </CStatefulContainer>
        </CModal>
      )}

      {/* Desktop Modal for clickToInteract */}
      {shouldShowDesktopModal && (
        <CModal
          open={!!state.showModal}
          style={{ paddingLeft: 0, paddingRight: 0 }}
          onClose={() => {
            updateStateAttribute<State>({ showModal: false }, setState);
            props.onCloseModal?.();
          }}
        >
          <CHorizontalScrollContainer>{modalPlot}</CHorizontalScrollContainer>
        </CModal>
      )}

      {props.dataTimestamp && (
        <CPageModule style={{ paddingBottom: 0, paddingTop: 0 }}>
          <p className={'text-right text-xs mt-4'}>
            <TimestampComponent timestamp={props.dataTimestamp} />
          </p>
        </CPageModule>
      )}
    </CStatefulContainer>
  );
}
